/**
 * Recovery Cases -- Aaim support (/support/recovery-cases).
 *
 * platform.aaim.com:9303 (Aaim-operator SPA). A member who verified the email
 * or phone on file opens a break-glass recovery case from the consumer
 * portal's /recover page (GH #4375). An Aaim support agent reaches the member
 * on that channel, works the case, and closes it here (GH #4409; Robert's
 * ruling of 2026-10-25 04:16Z).
 *
 *   GET  /api/v1/admin/recovery-cases                  needs recovery:read
 *   POST /api/v1/admin/recovery-cases/{caseId}/close   needs recovery:close
 *
 * platform-admin-api forwards both to consumer-api, which checks the
 * permission and writes an audit row naming the agent for every list read and
 * every close. Every case is in scope for a holder. The page reads the same
 * permissions from the session user only to choose what to show; the server
 * decides. Oldest first; Load more follows nextPageToken. Each list load
 * empties the list and the page token and starts a new generation, and an
 * answer is applied only while its generation is current: a slow first load
 * answering after the filter changed, or a Load more asked under an earlier
 * filter, is dropped. A case someone else closed first answers 409, and the
 * page reloads the list. The Reference column shows the first eight
 * characters of the case id, the reference the member is given on /recover
 * step 3.
 *
 * Types come from the generated consumer-anonymous-onboarding spec; no
 * view-model interfaces.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  BodyText,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  Select,
} from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/generated/consumer-anonymous-onboarding';
import { useAuthStore } from '../../stores/auth';
import { recoveryCaseAdminClient } from '../../api/typed-clients';
import { apiErrorMessage } from '../../api/error-message';
import { hasPermission } from '../../utils/role-helpers';

type RecoveryCaseAdminView = components['schemas']['RecoveryCaseAdminView'];
type RecoveryCaseStatus = components['schemas']['RecoveryCaseStatus'];
type RecoveryReason = components['schemas']['RecoveryReason'];

const READ_PERMISSION = 'recovery:read';
const CLOSE_PERMISSION = 'recovery:close';

const REASON_LABELS: Record<RecoveryReason, string> = {
  fi_unreachable: 'FI unreachable',
  switch_primary_fi: 'Switching primary FI',
  other: 'Other',
};

const STATUS_OPTIONS: { value: RecoveryCaseStatus | ''; label: string }[] = [
  { value: 'OPEN', label: 'Open' },
  { value: 'CLOSED', label: 'Closed' },
  { value: '', label: 'All' },
];

function formatTime(value: string | null): string {
  return value ? new Date(value).toLocaleString() : '';
}

/** The case reference /recover step 3 gives the member: the id's first eight characters. */
function caseReference(caseId: string): string {
  return caseId.slice(0, 8);
}

export function RecoveryCasesPage() {
  const permissions = useAuthStore((s) => s.user?.permissions);
  const mayRead = hasPermission(permissions, READ_PERMISSION);
  const mayClose = hasPermission(permissions, CLOSE_PERMISSION);

  const [statusFilter, setStatusFilter] = useState<RecoveryCaseStatus | ''>('OPEN');
  const [cases, setCases] = useState<RecoveryCaseAdminView[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [closingId, setClosingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const fetchPage = useCallback(
    async (pageToken?: string) => {
      const { data, error } = await recoveryCaseAdminClient.GET('/api/v1/admin/recovery-cases', {
        params: {
          query: {
            ...(statusFilter ? { status: statusFilter } : {}),
            ...(pageToken ? { pageToken } : {}),
          },
        },
      });
      if (error || !data) {
        throw new Error(apiErrorMessage(error) ?? 'Failed to load recovery cases');
      }
      return data.data;
    },
    [statusFilter],
  );

  // Bumped by every list load. A request remembers the generation it was
  // asked under, and its answer is dropped once a later load has started.
  const generationRef = useRef(0);

  const loadCases = useCallback(async () => {
    const generation = ++generationRef.current;
    if (!mayRead) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadingMore(false);
    setLoadError(null);
    setConfirmingId(null);
    setCases([]);
    setNextPageToken(null);
    try {
      const page = await fetchPage();
      if (generation !== generationRef.current) return;
      setCases(page.cases);
      setNextPageToken(page.nextPageToken);
    } catch (err) {
      if (generation !== generationRef.current) return;
      setLoadError(err instanceof Error ? err.message : 'Failed to load recovery cases');
      setCases([]);
      setNextPageToken(null);
    } finally {
      if (generation === generationRef.current) setLoading(false);
    }
  }, [mayRead, fetchPage]);

  useEffect(() => {
    void loadCases();
  }, [loadCases]);

  const loadMore = useCallback(async () => {
    if (!nextPageToken) return;
    const generation = generationRef.current;
    setLoadingMore(true);
    setLoadError(null);
    try {
      const page = await fetchPage(nextPageToken);
      if (generation !== generationRef.current) return;
      setCases((prev) => [...prev, ...page.cases]);
      setNextPageToken(page.nextPageToken);
    } catch (err) {
      if (generation !== generationRef.current) return;
      setLoadError(err instanceof Error ? err.message : 'Failed to load recovery cases');
    } finally {
      if (generation === generationRef.current) setLoadingMore(false);
    }
  }, [fetchPage, nextPageToken]);

  const closeCase = useCallback(
    async (caseId: string) => {
      setClosingId(caseId);
      setActionError(null);
      setActionMessage(null);
      try {
        const { data, error, response } = await recoveryCaseAdminClient.POST(
          '/api/v1/admin/recovery-cases/{caseId}/close',
          { params: { path: { caseId } } },
        );
        if (data) {
          setCases((prev) => prev.map((row) => (row.id === caseId ? data.data : row)));
          setActionMessage('Recovery case closed.');
          return;
        }
        if (response.status === 409 || response.status === 404) {
          setActionError(
            response.status === 409
              ? 'This case was already closed. The list has been reloaded.'
              : 'This case no longer exists. The list has been reloaded.',
          );
          await loadCases();
          return;
        }
        setActionError(apiErrorMessage(error) ?? 'Failed to close the recovery case');
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Failed to close the recovery case');
      } finally {
        setClosingId(null);
        setConfirmingId(null);
      }
    },
    [loadCases],
  );

  if (!mayRead) {
    return (
      <div className="p-6 space-y-4">
        <Card>
          <div className="p-4">
            <Alert type="error">
              Recovery cases are visible to Aaim support agents who hold the recovery:read
              permission.
            </Alert>
          </div>
        </Card>
      </div>
    );
  }

  const columns: DataTableColumn<RecoveryCaseAdminView>[] = [
    {
      header: 'Reference',
      accessor: 'id',
      cell: (_value, row) => <span className="font-mono text-xs">{caseReference(row.id)}</span>,
    },
    {
      header: 'Opened',
      accessor: 'openedAt',
      cell: (_value, row) => <span className="text-secondary text-sm">{formatTime(row.openedAt)}</span>,
    },
    {
      header: 'Status',
      accessor: 'status',
      cell: (_value, row) => (
        <Badge variant={row.status === 'OPEN' ? 'warning' : 'neutral'} size="sm">
          {row.status === 'OPEN' ? 'Open' : 'Closed'}
        </Badge>
      ),
    },
    {
      header: 'Reason',
      accessor: 'reason',
      cell: (_value, row) => REASON_LABELS[row.reason],
    },
    {
      header: 'Verified by',
      accessor: 'channel',
      cell: (_value, row) => (row.channel === 'email' ? 'Email' : 'Phone'),
    },
    {
      header: 'Contact',
      accessor: 'contact',
      cell: (_value, row) =>
        row.contact ?? <span className="text-secondary text-sm">None on file</span>,
    },
    {
      header: 'Member',
      accessor: 'userId',
      cell: (_value, row) => <span className="font-mono text-xs">{row.userId}</span>,
    },
    {
      header: 'Closed',
      accessor: 'closedAt',
      cell: (_value, row) => <span className="text-secondary text-sm">{formatTime(row.closedAt)}</span>,
    },
    {
      header: '',
      accessor: 'id',
      cell: (_value, row) => {
        if (row.status !== 'OPEN' || !mayClose) return null;
        if (confirmingId === row.id) {
          return (
            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => void closeCase(row.id)}
                disabled={closingId !== null}
              >
                {closingId === row.id ? 'Closing...' : 'Confirm close'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setConfirmingId(null)}
                disabled={closingId !== null}
              >
                Cancel
              </Button>
            </div>
          );
        }
        return (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setConfirmingId(row.id);
              setActionError(null);
              setActionMessage(null);
            }}
            disabled={closingId !== null}
          >
            Close case
          </Button>
        );
      },
    },
  ];

  return (
    <div className="p-6 space-y-4">
      <BodyText size="sm" color="secondary">
        Break-glass recovery cases, opened by members who verified the email or phone on file.
        Reach the member at the contact shown, then close the case. Every list read and every
        close is recorded in the audit log under your name.
      </BodyText>

      {loadError && <Alert type="error">{loadError}</Alert>}
      {actionError && <Alert type="error">{actionError}</Alert>}
      {actionMessage && <Alert type="success">{actionMessage}</Alert>}

      <Card>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Status"
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(STATUS_OPTIONS.find((o) => o.value === e.target.value)?.value ?? 'OPEN')
              }
            />
          </div>

          <DataTable<RecoveryCaseAdminView>
            columns={columns}
            data={cases}
            rowKey="id"
            striped
            hoverable
            emptyMessage={loading ? 'Loading...' : 'No recovery cases match the current filter.'}
          />

          {nextPageToken && !loading && (
            <Button variant="secondary" onClick={() => void loadMore()} disabled={loadingMore}>
              {loadingMore ? 'Loading...' : 'Load more'}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
